import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import { useLocation, Outlet } from "react-router-dom";
import './EditStore.css';
import { useNavigate } from "react-router-dom";
import p from './images/profile.jpg';
import cam from './images/cam.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-f7ce.up.railway.app';
const SLOW_THRESHOLD = 2000; // ms

// ── Module-level cache ────────────────────────────────────────────────────────
// Key: pageId (string) → { profileImage, bannerImage, title, description, followText, aboutData, shopItems, shopCats, ts }
const _storeCache = new Map();
const STORE_TTL   = 10 * 60 * 1000; // 10 minutes

// ── Per-page localStorage helpers (avoids cross-page bleed) ──────────────────
function _lsSetImage(pageId, type, url) {
  // type: 'profile' | 'banner'
  localStorage.setItem(`img_${type}_${pageId}`, url);
}
function _lsGetImage(pageId, type) {
  return localStorage.getItem(`img_${type}_${pageId}`) || null;
}
function _lsClearImages(pageId) {
  localStorage.removeItem(`img_profile_${pageId}`);
  localStorage.removeItem(`img_banner_${pageId}`);
}
// Legacy key cleanup — remove old single-slot keys on first run
(function _cleanLegacyImageKeys() {
  ['cached_profile_image', 'cached_banner_image', 'cached_image_page_id'].forEach(k =>
    localStorage.removeItem(k)
  );
})();

// ── storageGet: reads { val, exp } TTL-wrapped values from localStorage ───────
const storageGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Support both plain objects (store_header) and TTL-wrapped { val, exp }
    if (parsed && typeof parsed === 'object' && 'val' in parsed) {
      return Date.now() < parsed.exp ? parsed.val : null;
    }
    return parsed;
  } catch { return null; }
};

// ── Preload image into browser cache before React renders the <img> ───────────
// Mirrors Store.js — tells the browser to fetch the image NOW so by the time
// the <img> tag renders, the image is already in cache (zero-flash).
function preloadImage(url) {
  if (!url || url === p) return;
  try {
    if (document.querySelector(`link[rel="preload"][href="${url}"]`)) return;
    const link = document.createElement('link');
    link.rel  = 'preload';
    link.as   = 'image';
    link.href = url;
    document.head.appendChild(link);
  } catch {}
}

// ── Slow-fetch logger ─────────────────────────────────────────────────────────
function _warnSlow(label, elapsed, extra = "") {
  if (elapsed > SLOW_THRESHOLD) {
    console.error(`[SLOW][EditStore] ${label} took ${(elapsed / 1000).toFixed(2)}s (>${SLOW_THRESHOLD / 1000}s). ${extra}`);
  } else {
    console.debug(`[TIMING][EditStore] ${label} — ${elapsed.toFixed(0)}ms`);
  }
}

// ── Per-fetch timeout wrapper ─────────────────────────────────────────────────
function fetchWithTimeout(url, options = {}, ms = 8000) {
  const controller = new AbortController();
  const parentSignal = options.signal;
  const timeoutId = setTimeout(() => controller.abort(), ms);
  if (parentSignal && !parentSignal.aborted) {
    parentSignal.addEventListener('abort', () => controller.abort());
  }
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timeoutId)
  );
}

// ── [PERF] Pre-warm cache before navigation ───────────────────────────────────
export function preloadStoreImages(pageId, token) {
  if (!pageId) return;
  const key    = String(pageId);
  const cached = _storeCache.get(key);
  // Already fully warm — skip
  if (cached && cached.profileImage && cached.bannerImage !== undefined &&
      Date.now() - cached.ts < STORE_TTL) return;

  const hdrs = { "Authorization": `Bearer ${token}` };

  Promise.allSettled([
    fetch(`${API_URL}/api/get_profile_image?pageid=${pageId}`, { headers: hdrs }),
    fetch(`${API_URL}/api/get_banner_image?pageid=${pageId}`,  { headers: hdrs }),
  ]).then(async ([profileRes, bannerRes]) => {
    let img    = null;
    let banner = null;
    if (profileRes.status === 'fulfilled' && profileRes.value.ok) {
      try { const d = await profileRes.value.json(); img = d.profile_image_url || null; } catch (_) {}
    }
    if (bannerRes.status === 'fulfilled' && bannerRes.value.ok) {
      try { const d = await bannerRes.value.json(); banner = d.banner_image_url || null; } catch (_) {}
    }
    // Merge into _storeCache — EditStore's useState will read this on mount
    const existing = _storeCache.get(key) || {};
    _storeCache.set(key, {
      ...existing,
      profileImage: img    || existing.profileImage || null,
      bannerImage:  banner !== null ? banner : (existing.bannerImage ?? ''),
      ts:           existing.ts || Date.now(),
    });
    // Seed per-page localStorage so it survives a hard refresh
    if (img)    _lsSetImage(key, 'profile', img);
    if (banner) _lsSetImage(key, 'banner',  banner);
  }).catch(() => {});
}

function EditStore({ userEmail, pageName }) {
  const location = useLocation();

  // ── Router state is the authoritative source for pageId on navigation ────────
  const _routerState = location.state || {};

  // ── Read pageId — router state first, then localStorage ──────────────────────
  const _initPageId = (() => {
    const id = _routerState.pageId || localStorage.getItem('pageId') || null;
    if (id) localStorage.setItem('pageId', String(id));
    return id ? String(id) : null;
  })();

  // ── Resolve initial images/title/desc — same priority order as Store.js ──────
  // 1. location.state (passed by Pages.js navigate call)
  // 2. store_header:{pageId} in localStorage (written by Store.js on every visit)
  // 3. per-page img_ keys (written by EditStore after upload)
  // 4. module-level _storeCache (session-only)
  // 5. defaults
  const _storedHeader  = _initPageId ? storageGet(`store_header:${_initPageId}`) : null;
  const _memCache      = _initPageId ? _storeCache.get(_initPageId) : null;

  const _initProfile = _routerState.profile_image_url
    || _storedHeader?.profile_image_url
    || (_memCache?.profileImage !== p ? _memCache?.profileImage : null)
    || _lsGetImage(_initPageId, 'profile')
    || p;

  const _initBanner = _routerState.banner_image_url
    || _storedHeader?.banner_image_url
    || _memCache?.bannerImage
    || _lsGetImage(_initPageId, 'banner')
    || '';

  const _initTitle = _routerState.pagename
    || _storedHeader?.pagename
    || _memCache?.title
    || localStorage.getItem('pagename')
    || "Default Title";

const _initDesc = _routerState.description
  || _storedHeader?.description
  || _memCache?.description
  || "";  // empty string is safer than a misleading default

  // ── Preload images synchronously before first render — eliminates flash ───────
  const _preloadedRef = useRef(false);
  if (!_preloadedRef.current) {
    _preloadedRef.current = true;
    preloadImage(_initProfile);
    preloadImage(_initBanner);
  }

  const [title, setTitle]             = useState(_initTitle);
  const [description, setDescription] = useState(_initDesc);
  const [pageId, setPageId]           = useState(_initPageId);
  const [profileImage, setProfileImage] = useState(_initProfile);
  const [bannerImage, setBannerImage]   = useState(_initBanner);
  const [followText, setFollowText]   = useState("+ Follow");
  const [activeTab, setActiveTab]     = useState(localStorage.getItem('activeTab') || "About");
  const navigate = useNavigate();

  const token = localStorage.getItem('token'); // Get token from localStorage
  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
  const [menuOpen, setMenuOpen]       = useState(false);
  const [userId, setUserId]           = useState(localStorage.getItem('user_id') || null);
  const abortRef = useRef(null);

  // Prefetched tab data passed down to children via Outlet context
  const [aboutData, setAboutData] = useState(() => {
    try { const s = localStorage.getItem('cached_about_data'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [shopItems, setShopItems]       = useState(null);
  const [shopCats, setShopCats]         = useState(null);
  const [prefetchDone, setPrefetchDone] = useState(() => !!localStorage.getItem('cached_about_data'));

  const [showUploadForm, setShowUploadForm]             = useState(false);
  const [selectedFile, setSelectedFile]                 = useState(null);
  const [showBannerUploadForm, setShowBannerUploadForm] = useState(false);
  const [selectedBannerFile, setSelectedBannerFile]     = useState(null);
  const [rerender, setRerender]                         = useState(false);

  // ── When pageId changes, bust stale cache and sync localStorage ─────────────
  useEffect(() => {
    if (!pageId) return;
    const key = String(pageId);
    localStorage.setItem('pageId', key);
    // Restore cached images for this page immediately so they appear on first paint.
    // Do NOT delete _storeCache here — that would force a blank flash.
    // The Phase-1 prefetch effect always fetches fresh and will update state when done.
    const cachedProfile = _lsGetImage(key, 'profile')
      || storageGet(`store_header:${key}`)?.profile_image_url
      || _routerState.profile_image_url;
    const cachedBanner = _lsGetImage(key, 'banner')
      || storageGet(`store_header:${key}`)?.banner_image_url
      || _routerState.banner_image_url;
    if (cachedProfile) {
      setProfileImage(cachedProfile);
      preloadImage(cachedProfile);
    } else {
      setProfileImage(p);
    }
    if (cachedBanner) {
      setBannerImage(cachedBanner);
      preloadImage(cachedBanner);
    } else {
      setBannerImage('');
    }
  }, [pageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── userId: read from localStorage first (avoids a serial network hop) ──────
  useEffect(() => {
    if (localStorage.getItem('user_id')) return;
    const controller = new AbortController();
    const fetchUserId = async () => {
      const t0 = performance.now();
      try {
        const userResponse = await fetchWithTimeout(
          `${API_URL}/api/get-user-id`,
          { signal: controller.signal, headers: { "Authorization": `Bearer ${token}` } },
          8000
        );
        _warnSlow("GET /api/get-user-id", performance.now() - t0);
        if (!userResponse.ok) throw new Error(`Error fetching user ID: ${userResponse.statusText}`);
        const userData = await userResponse.json();
        localStorage.setItem('user_id', userData.user_id);
        setUserId(userData.user_id);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error("[EditStore] Error fetching user ID:", error);
      }
    };
    fetchUserId();
    return () => controller.abort();
  }, [token]);

  // ── [PERF] Prefetch Phase 1: fire on pageId alone, don't wait for userId ────
  useEffect(() => {
    if (!pageId) return;

    const key = String(pageId);

    // For images: ALWAYS fetch fresh from DB — never serve stale cache for images.
    // Only non-image data (followText, aboutData, shopCats) may be served from cache.
    const cached = _storeCache.get(key);
    if (cached && Date.now() - cached.ts < STORE_TTL) {
      setFollowText(cached.followText || "+ Follow");
      if (cached.aboutData) localStorage.setItem('cached_about_data', JSON.stringify(cached.aboutData));
      setAboutData(cached.aboutData);
      setShopItems(cached.shopItems);
      setShopCats(cached.shopCats);
      // Don't return early — fall through to always re-fetch images from DB
    }

    // ── Only reset images if we have NO cached image for this page.
    // If we already have a good URL (from router state / store_header / localStorage),
    // keep it visible while the network re-validates — eliminates the flash.
    const cachedProfile = _lsGetImage(key, 'profile') || _storedHeader?.profile_image_url || _routerState.profile_image_url;
    const cachedBanner  = _lsGetImage(key, 'banner')  || _storedHeader?.banner_image_url  || _routerState.banner_image_url;
    if (!cachedProfile) setProfileImage(p);
    if (!cachedBanner)  setBannerImage('');

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;

    const hdrs = { "Authorization": `Bearer ${token}` };
    const t0   = performance.now();

    Promise.allSettled([
      fetchWithTimeout(`${API_URL}/api/follow-status?page_id=${pageId}`,         { signal, headers: hdrs }, 8000),
fetchWithTimeout(`${API_URL}/api/get_store_header?pageid=${pageId}`, { signal }, 8000),
fetchWithTimeout(`${API_URL}/get_about_page_for_store?pageid=${pageId}`, { signal }, 8000),
      fetchWithTimeout(`${API_URL}/api/categories/editshop`,                      { signal, headers: hdrs }, 8000),
    ]).then(async ([followRes, headerRes, aboutRes, catsRes]) => {
      if (signal.aborted) return;

      const elapsed      = performance.now() - t0;
      const anySucceeded = [followRes, headerRes, aboutRes, catsRes]
        .some(r => r.status === "fulfilled");
      if (anySucceeded) {
        _warnSlow(`Phase-1 parallel prefetch (pageId=${pageId})`, elapsed,
          "Check DB indexes and Railway cold-start.");
      }

      // follow status
      let ft = "+ Follow";
      if (followRes.status === "fulfilled" && followRes.value.ok) {
        try { const d = await followRes.value.json(); ft = d.following ? "✓ Following" : "+ Follow"; } catch (_) {}
      } else if (followRes.status === "rejected" && followRes.reason?.name !== 'AbortError') {
        console.error("[EditStore] follow-status fetch failed:", followRes.reason);
      }

      // store header (title/description/profile/banner in one call)
      let img = null;
      let banner = null;
      let pageTitle = null;
      let pageDescription = null;
      if (headerRes.status === "fulfilled" && headerRes.value.ok) {
        try {
          const d = await headerRes.value.json();
          img = d.profile_image_url || null;
          banner = d.banner_image_url || null;
          pageTitle = d.pagename || null;
          pageDescription = d.description || null;
        } catch (_) {}
      } else if (headerRes.status === "rejected" && headerRes.reason?.name !== 'AbortError') {
        console.error("[EditStore] get_store_header fetch failed:", headerRes.reason);
      }

      // about data
      let about = null;
      if (aboutRes.status === "fulfilled" && aboutRes.value.ok) {
        try {
          const d = await aboutRes.value.json();
          about = {
            paragraphs:    d.paragraphs    || [],
            website:       d.website       || "",
            verified_page: d.verified_page || "",
            industry:      d.industry      || "",
            company:       d.company       || "",
            headquarter:   d.headquarter   || "",
            founded:       d.founded       || "",
            specialties:   d.specialties   || "",
            address:       d.address       || "",
            country:       d.country       || "",
            state:         d.state         || "",
            city:          d.city          || "",
            zipCode:       d.zipcode       || "",
            page_type:     d.page_type     || "",
          };
        } catch (_) {}
      } else if (aboutRes.status === "rejected" && aboutRes.reason?.name !== 'AbortError') {
        console.error("[EditStore] get_about_page_for_store fetch failed:", aboutRes.reason);
      } else if (aboutRes.status === "fulfilled" && !aboutRes.value.ok) {
        console.error(`[EditStore] get_about_page_for_store HTTP ${aboutRes.value.status} pageid=${pageId}`);
      }

      // shop categories
      let cats = null;
      if (catsRes.status === "fulfilled" && catsRes.value.ok) {
        try {
          const d = await catsRes.value.json();
          cats = (d.categories || []).map(c => ({ value: c, label: c }));
        } catch (_) {}
      } else if (catsRes.status === "rejected" && catsRes.reason?.name !== 'AbortError') {
        console.error("[EditStore] categories/editshop fetch failed:", catsRes.reason);
      }

      if (img)    _lsSetImage(key, 'profile', img);
      if (banner) _lsSetImage(key, 'banner',  banner);
      if (about) localStorage.setItem('cached_about_data', JSON.stringify(about));

      // Write store_header:{pageId} — same key Store.js reads for instant first paint
      // So next time either EditStore or Store loads this page, images are instant
      localStorage.setItem(`store_header:${key}`, JSON.stringify({
        pagename:          pageTitle || localStorage.getItem('pagename') || "",
        description:       pageDescription || localStorage.getItem('description') || "",
        profile_image_url: img    || '',
        banner_image_url:  banner || '',
      }));

      _storeCache.set(key, {
        followText:   ft,
        profileImage: img    || p,
        bannerImage:  banner || '',
        title:       pageTitle || "",
        description: pageDescription || "",
        aboutData:    about,
        shopItems:    null,
        shopCats:     cats,
        ts:           Date.now(),
      });

      setFollowText(ft);
      if (img)    setProfileImage(img);
      if (banner) setBannerImage(banner);
      if (pageTitle) setTitle(pageTitle);
      if (pageDescription !== null) setDescription(pageDescription);
      setAboutData(about);
      setShopCats(cats);
      if (anySucceeded) setPrefetchDone(true);
    }).catch(e => {
      if (e.name !== 'AbortError') {
        console.error("[EditStore] Phase-1 prefetch error:", e);
        setPrefetchDone(true);
      }
    });

    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [pageId, token]);

  // ── [PERF] Phase 2: shop items — requires userId ──────────────────────────
  useEffect(() => {
    if (!userId || !pageId) return;
    const key    = String(pageId);
    const cached = _storeCache.get(key);
    if (cached && cached.shopItems !== null) {
      setShopItems(cached.shopItems);
      return;
    }
    const controller = new AbortController();
    const t0 = performance.now();
    fetchWithTimeout(
      `${API_URL}/api/get_edit_items?userid=${userId}&pageid=${pageId}`,
      { signal: controller.signal, headers: { "Authorization": `Bearer ${token}` } },
      8000
    ).then(async res => {
      _warnSlow(`GET /api/get_edit_items (pageId=${pageId})`, performance.now() - t0);
      if (!res.ok) { console.error(`[EditStore] get_edit_items HTTP ${res.status}`); return; }
      const items = await res.json();
      setShopItems(items);
      const c = _storeCache.get(key);
      if (c) _storeCache.set(key, { ...c, shopItems: items });
    }).catch(e => {
      if (e.name !== 'AbortError') console.error("[EditStore] get_edit_items fetch failed:", e);
    });
    return () => controller.abort();
  }, [userId, pageId, token]);

  // ── Page ID lookup ────────────────────────────────────────────────────────
  const getPageIdAndStore = (pageName) => {
    fetch(`${API_URL}/api/pageid-id?page_name=${pageName}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
    .then(data => {
      if (data.pageId) {
        localStorage.setItem('pageId', data.pageId);
        setPageId(data.pageId);
      } else {
        console.error('[EditStore] Page ID not found:', data.error);
      }
    })
    .catch(error => console.error('[EditStore] Error fetching page ID:', error));
  };

  const fetchProfileImage = async () => {
    try {
      const t0 = performance.now();
      const response = await fetchWithTimeout(
        `${API_URL}/api/get_profile_image?pageid=${pageId}`,
        { headers: { "Authorization": `Bearer ${token}` } }, 8000
      );
      _warnSlow("GET /api/get_profile_image (manual refresh)", performance.now() - t0);
      if (response.ok) {
        const data = await response.json();
        if (data.profile_image_url) {
          setProfileImage(data.profile_image_url);
          _lsSetImage(String(pageId), 'profile', data.profile_image_url);
          const c = _storeCache.get(String(pageId));
          if (c) _storeCache.set(String(pageId), { ...c, profileImage: data.profile_image_url });
        } else { setProfileImage(p); }
      } else { setProfileImage(p); }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("[EditStore] fetchProfileImage error:", error);
      setProfileImage(p);
    }
  };

  const fetchBannerImage = async () => {
    try {
      const t0 = performance.now();
      const response = await fetchWithTimeout(
        `${API_URL}/api/get_banner_image?pageid=${pageId}`,
        { headers: { "Authorization": `Bearer ${token}` } }, 8000
      );
      _warnSlow("GET /api/get_banner_image (manual refresh)", performance.now() - t0);
      if (response.ok) {
        const data = await response.json();
        if (data.banner_image_url) {
          setBannerImage(data.banner_image_url);
          _lsSetImage(String(pageId), 'banner', data.banner_image_url);
          const c = _storeCache.get(String(pageId));
          if (c) _storeCache.set(String(pageId), { ...c, bannerImage: data.banner_image_url });
        } else { setBannerImage(''); }
      } else { setBannerImage(''); }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("[EditStore] fetchBannerImage error:", error);
      setBannerImage('');
    }
  };

  const checkFollowStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/follow-status?page_id=${pageId}`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await response.json();
      setFollowText(data.following ? "✓ Following" : "+ Follow");
    } catch (error) {
      console.error("[EditStore] Error fetching follow status:", error);
    }
  };

  const fetchPageDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/page/get_page_details?pageid=${pageId}`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setTitle(data.pagename);
        setDescription(data.description);
      } else {
        console.error("[EditStore] Error fetching page details:", data.error);
      }
    } catch (error) {
      console.error("[EditStore] Error fetching page details:", error);
    }
  };

  const handleFollowClick = async () => {
    if (!userId || !pageId) {
      console.error("[EditStore] handleFollowClick — User ID or Page ID not available");
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/follow`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, page_id: pageId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update follow status");
      const ft = data.following ? "✓ Following" : "+ Follow";
      setFollowText(ft);
      const c = _storeCache.get(String(pageId));
      if (c) _storeCache.set(String(pageId), { ...c, followText: ft });
    } catch (error) {
      console.error("[EditStore] handleFollowClick error:", error.message);
    }
  };

  // ── CHANGED: Home tab removed; About is now the first tab ──
  const tabs = [
    { name: "About", path: "editabout" },
    { name: "Posts", path: "editposts" },
    { name: "Shop",  path: "editshop" },
  ];

  useEffect(() => {
    const currentPath = location.pathname.split('/').pop();
    const matchingTab = tabs.find(tab => tab.path === currentPath);
    if (matchingTab) {
      setActiveTab(matchingTab.name);
      localStorage.setItem('activeTab', matchingTab.name);
    }
  }, [location.pathname]);

  // ── Navigate to About by default if no tab path is active ──
  useEffect(() => {
    const currentPath = location.pathname.split('/').pop();
    const isTabPath = tabs.some(tab => tab.path === currentPath);
    if (!isTabPath) {
      navigate("editabout", { replace: true });
    }
  }, []);

  const handleTabClick = (tab) => {
    try {
      setActiveTab(tab.name);
      localStorage.setItem('activeTab', tab.name);
      navigate(tab.path);
    } catch (error) {
      console.error("[EditStore] Navigation failed:", error);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {};
      reader.readAsDataURL(file);
    }
  };

  const handleBannerImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {};
      reader.readAsDataURL(file);
    }
  };

  const handleDone = async () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append('page_id', pageId);
      formData.append('profile_image', selectedFile);
      try {
        const response = await fetch(`${API_URL}/api/update_profile_image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        const data = await response.json();
        if (response.ok) {
          const newUrl = data.profile_image_url;
          preloadImage(newUrl);
          setProfileImage(newUrl);
          _lsSetImage(String(pageId), 'profile', newUrl);
          // Keep store_header in sync so next mount is instant
          const _sh = storageGet(`store_header:${pageId}`) || {};
          localStorage.setItem(`store_header:${pageId}`, JSON.stringify({ ..._sh, profile_image_url: newUrl }));
          _storeCache.delete(String(pageId));
        } else {
          console.error('[EditStore] Error updating profile image:', data);
        }
      } catch (error) {
        console.error('[EditStore] Error during profile image upload:', error);
      } finally {
        setShowUploadForm(false);
        setSelectedFile(null);
      }
    } else {
      setShowUploadForm(false);
    }
  };

  const handleBannerDone = async () => {
    if (selectedBannerFile) {
      const formData = new FormData();
      formData.append('page_id', pageId);
      formData.append('banner_image', selectedBannerFile);
      try {
        const response = await fetch(`${API_URL}/api/update_banner_image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        const data = await response.json();
        if (response.ok) {
          const newUrl = data.banner_image_url;
          preloadImage(newUrl);
          setBannerImage(newUrl);
          _lsSetImage(String(pageId), 'banner', newUrl);
          // Keep store_header in sync so next mount is instant
          const _sh = storageGet(`store_header:${pageId}`) || {};
          localStorage.setItem(`store_header:${pageId}`, JSON.stringify({ ..._sh, banner_image_url: newUrl }));
          _storeCache.delete(String(pageId));
        } else {
          console.error('[EditStore] Error updating banner image:', data);
        }
      } catch (error) {
        console.error('[EditStore] Error during banner image upload:', error);
      } finally {
        setShowBannerUploadForm(false);
        setSelectedBannerFile(null);
        setRerender(!rerender);
      }
    } else {
      setShowBannerUploadForm(false);
    }
  };

  const handleCameraClick = () => setShowUploadForm(true);
  const handleCloseModal  = () => setShowUploadForm(false);

  return (
    <>
      <Header />
      <div className="profile_container-editstore">
        <div className="profile-banner-editstore" style={{ backgroundImage: `url(${bannerImage})` }}>
          <button onClick={() => setShowBannerUploadForm(true)}>
            <img src={cam} alt="Change Banner" style={{ width: '24px', height: '24px' }} />
          </button>
        </div>
        <div className="profile-content-editstore">
          <div className="profile-image-editstore">
            <img src={profileImage} alt="Profile Avatar" loading="eager" fetchPriority="high" />
            <button onClick={handleCameraClick}>
              <img src={cam} alt="Camera Icon" />
            </button>
          </div>
          <div className="profile-details-editstore">
            <h1 className="profile-name-editstore">{title}</h1>
            <p className="profile-info-editstore">{description}</p>
          </div>
          <div className="actions-editstore">
            <button className="follow-btn-editstore" onClick={handleFollowClick}>
              {followText}
            </button>
          </div>
        </div>
        <div className={menuOpen ? "open" : ""}>
          <div className="navigation-tabs-editstore">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => handleTabClick(tab)}
                className={activeTab === tab.name ? "active-tab" : ""}
                aria-current={activeTab === tab.name ? "page" : undefined}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>
        <Outlet context={{ userId, pageId, token, aboutData, shopItems, shopCats, prefetchDone }} />
      </div>

      {showUploadForm && (
        <div className="modal-img">
          <div className="modal-content-img">
            <span className="close" onClick={handleCloseModal}>&times;</span>
            <h2>Upload Profile Image</h2>
            <input type="file" accept="image/*" onChange={handleImageUpload} style={{ marginTop: '10px' }} />
            <button onClick={handleDone} style={{ marginTop: '10px' }}>Done</button>
          </div>
        </div>
      )}

      {showBannerUploadForm && (
        <div className="modal-img">
          <div className="modal-content-img">
            <span className="close" onClick={() => setShowBannerUploadForm(false)}>&times;</span>
            <h2>Upload Banner Image</h2>
            <input type="file" accept="image/*" onChange={handleBannerImageUpload} style={{ marginTop: '10px' }} />
            <button onClick={handleBannerDone} style={{ marginTop: '10px' }}>Done</button>
          </div>
        </div>
      )}
    </>
  );
}

export default EditStore;